import { useEffect, useState } from 'react';

import type { SyntheticEvent } from 'react';

import { Box, IconButton, Slider, styled, Typography } from '@mui/material';

import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';

import { FormatListBulletedIcon, GridViewIcon } from '@libs/shared/ui/icons';

import type { ListMode } from '../ParticipantList.consts';
import { ParticipantListConsts } from '../ParticipantList.consts';

import { ActionsWrapper } from './ListModeAction.styles';

type Props = {
  listMode: ListMode;
  defaultMachineIframeSize: number;
  onListModeChange: (listMode: ListMode) => void;
  onMachineIframeSizeChange: (properties: number) => void;
  onSaveMachineIframeSize: (machineIframeSize: number) => void;
};

const sizeToLabelMap: Record<number, string> = {
  1: 'XS',
  2: 'S',
  3: 'M',
  4: 'L',
  5: 'XL',
  6: 'XXL'
};

const sizeToColumns = (size: number): number => {
  const mapping: Record<number, number> = {
    1: 6,
    2: 5,
    3: 4,
    4: 3,
    5: 2,
    6: 1
  };

  return mapping[size] || 4;
};

const SliderContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${Spacing.small};
  min-width: 200px;
`;

const SizeLabel = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${Colors.darkGrey};
  min-width: 24px;
  text-align: center;
`;

const StyledIconButton = styled(IconButton)<{ active: boolean }>`
  color: ${({ active }) => (active ? Colors.textColor : Colors.darkGrey)} !important;

  &:hover {
    color: ${Colors.blue} !important;
  }
`;

export const ListModeActions = ({
  listMode,
  defaultMachineIframeSize,
  onListModeChange,
  onMachineIframeSizeChange,
  onSaveMachineIframeSize
}: Readonly<Props>) => {
  const [machineIframeSize, setMachineIframeSize] = useState(defaultMachineIframeSize || 4);

  const handleBasicInfoModeClick = () => onListModeChange(ParticipantListConsts.listMode.basicInfo);

  const handleLivePreviewModeClick = () =>
    onListModeChange(ParticipantListConsts.listMode.livePreview);

  const handleChangeMachineIframeSize = (event: Event, value: number | number[]) => {
    const newSize = value as number;
    setMachineIframeSize(newSize);
    onMachineIframeSizeChange(sizeToColumns(newSize));
  };

  const handleSaveMachineIframeSize = (event: SyntheticEvent | Event, value: number | number[]) => {
    const newSize = value as number;
    if (newSize !== defaultMachineIframeSize) {
      onSaveMachineIframeSize(newSize);
    }
  };

  const valueLabelFormat = (value: number) => sizeToLabelMap[value] || value.toString();

  useEffect(() => {
    if (defaultMachineIframeSize) {
      setMachineIframeSize(defaultMachineIframeSize);
      onMachineIframeSizeChange(sizeToColumns(defaultMachineIframeSize));
    }
  }, [defaultMachineIframeSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ActionsWrapper>
      {listMode === ParticipantListConsts.listMode.livePreview ? (
        <SliderContainer>
          <SizeLabel>XS</SizeLabel>
          <Slider
            value={machineIframeSize}
            min={1}
            step={1}
            max={6}
            getAriaValueText={valueLabelFormat}
            valueLabelFormat={valueLabelFormat}
            onChange={handleChangeMachineIframeSize}
            onChangeCommitted={handleSaveMachineIframeSize}
            valueLabelDisplay="auto"
            aria-labelledby="machine-size-slider"
            sx={{ flex: 1 }}
          />
          <SizeLabel>XXL</SizeLabel>
        </SliderContainer>
      ) : null}
      <StyledIconButton
        onClick={handleLivePreviewModeClick}
        active={listMode === ParticipantListConsts.listMode.livePreview}
      >
        <GridViewIcon fontSize="large" />
      </StyledIconButton>
      <StyledIconButton
        onClick={handleBasicInfoModeClick}
        active={listMode === ParticipantListConsts.listMode.basicInfo}
      >
        <FormatListBulletedIcon fontSize="large" />
      </StyledIconButton>
    </ActionsWrapper>
  );
};
